import { Rndm } from './Rndm.js';
import { Arry } from './Arry.js';

//note name → frequency, 4 octaves from C2
const NOTE_FREQ: Record<string, number> = (() => {
    const names = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
    const out: Record<string, number> = {};
    for (let oct = 2; oct <= 6; oct++)
        names.forEach((n, i) => { out[`${n}${oct}`] = 440 * Math.pow(2, (oct - 4) + (i - 9) / 12); });
    return out;
})();

export type synth_opts = {
    note?: string;        //e.g. "A4", "C#3" — overrides hz
    hz?: number;
    type?: OscillatorType;
    attack?: number;      //seconds
    decay?: number;
    sustain?: number;     //gain level after decay
    release?: number;
    volume?: number;
};

let _audio_ready = false;
export function set_audio_ready(): void { _audio_ready = true; }
window.addEventListener("click", () => { _audio_ready = true; });
window.addEventListener("touchstart", () => { _audio_ready = true; }, { capture: true });

let _synth_ctx: AudioContext | null = null;
function synth_ctx(): AudioContext {
    if (!_synth_ctx) _synth_ctx = new AudioContext();
    return _synth_ctx;
}

export function Synth(opts: synth_opts = {}): void {
    if (!_audio_ready) return;
    const ctx = synth_ctx();
    if (ctx.state === 'suspended') ctx.resume();
    const hz = opts.note ? (NOTE_FREQ[opts.note] ?? 440) : (opts.hz ?? 440);
    const attack  = opts.attack  ?? 0.005;
    const decay   = opts.decay   ?? 0.1;
    const sustain = opts.sustain ?? 0.0;
    const release = opts.release ?? 0.3;
    const vol     = opts.volume  ?? 0.4;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = opts.type ?? 'sine';
    osc.frequency.value = hz;
    osc.connect(gain);
    gain.connect(ctx.destination);

    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(vol, now + attack);
    gain.gain.linearRampToValueAtTime(sustain * vol, now + attack + decay);
    gain.gain.setValueAtTime(sustain * vol, now + attack + decay);
    gain.gain.linearRampToValueAtTime(0, now + attack + decay + release);

    osc.start(now);
    osc.stop(now + attack + decay + release + 0.01);
}

export type toen_type = {
    id: string;
    buffer: AudioBuffer;
    source: AudioBufferSourceNode | null;
    gain: GainNode;
    play: () => void;
    stop: () => void;
    pause: () => void;
    set_volume: (n: number) => void;
}

export type toen_manager_type = {
    list: toen_type[];
    on: boolean;
    get: (id: string) => toen_type;
    make: (io: toen_type_input) => toen_type;
    preload: (ia: toen_type_input[]) => Promise<void>;
    init: () => void;
    play(id: string): void;
}

export type toen_type_input = {
    id?: string;
    file: string;
    volume?: number;
    loop?: boolean;
    on_end?: () => void;
}

export function toen_manager(auto_start = true): toen_manager_type {

    let ctx: AudioContext | null = null;

    const Toen: toen_manager_type = {
        list: [] as toen_type[],
        on: false,

        get: function (id: string) {
            return Arry.get.id(this.list, id);
        },

        make: function (io: toen_type_input) {

            const g = ctx!.createGain();
            g.gain.value = io.volume ?? 1;
            g.connect(ctx!.destination);

            const t: toen_type = {

                id: io.id || Rndm.id("toen_"),
                buffer: null as any,
                source: null,
                gain: g,

                play: function () {
                    if (!_audio_ready || !ctx || !this.buffer) return;
                    if (this.source) { try { this.source.stop(); } catch (_) { } }
                    const s = ctx.createBufferSource();
                    s.buffer = this.buffer;
                    s.loop = io.loop ?? false;
                    // source → gain → destination (gain node is reused, not recreated)
                    s.connect(this.gain);
                    s.onended = () => { this.source = null; if (io.on_end) io.on_end(); };
                    s.start();
                    this.source = s;
                },

                pause: function () {
                    this.stop();
                },

                stop: function () {
                    if (this.source) { try { this.source.stop(); } catch (_) { } this.source = null; }
                },

                set_volume: function (n: number) {
                    this.gain.gain.value = n;
                }
            };
            this.list.push(t);
            return t;
        },

        preload: async function (ia: toen_type_input[]) {
            if (!ctx) Toen.init();
            for (const input of ia) {
                const t = Toen.make(input);
                try {
                    const response = await fetch(input.file);
                    const arrayBuffer = await response.arrayBuffer();
                    t.buffer = await ctx!.decodeAudioData(arrayBuffer);
                } catch (e) {
                    console.warn("Toen: failed to load", input.file, e);
                }
            }
        },

        init: function () {
            if (ctx) return;
            ctx = new AudioContext();
            this.on = true;
        },

        play(id: string) {
            const t = this.get(id);
            if (t) t.play();
        }
    }

    if (auto_start) {
        const resume = function () {
            _audio_ready = true;
            Toen.init();
            if (ctx && ctx.state === "suspended") ctx.resume();
        };
        window.addEventListener("click", resume);
        window.addEventListener("touchstart", resume, { capture: true });
    }

    return Toen;
}